<template>
  <div>
    <!-- Count on the left, the add action on the right -->
    <div class="kb-list-head">
      <span class="kb-row-meta">
        <template v-if="membersLoading">
          Loading members…
        </template>
        <template v-else-if="members.length === 0">
          {{ props.isRedshirtAdmin ? 'No red shirts in this SubKrewe' : 'No members in this SubKrewe' }}
        </template>
        <template v-else>
          {{ memberCountLabel }}
        </template>
      </span>
      <div class="flex flex-wrap items-center gap-1">
        <!-- Extra list actions from the parent (the drawer's Email members) -->
        <slot name="actions" />
        <Button
          type="button"
          icon="pi pi-plus"
          text
          size="small"
          :label="props.isRedshirtAdmin ? 'Add red shirt' : 'Add member'"
          @click="showAddMemberDialog = true"
        />
      </div>
    </div>

    <KbSkeletonRows
      v-if="membersLoading"
      :rows="5"
      aside
      label="Loading members"
    />

    <!-- A row opens the member; its other actions show on hover or focus -->
    <DataTable
      v-if="!membersLoading && members.length > 0"
      :value="members"
      size="small"
      class="kb-sk-members cursor-pointer"
      selection-mode="single"
      data-key="id"
      :paginator="members.length > 10"
      :rows="10"
      sort-field="name"
      :sort-order="1"
      @row-click="onRowClick"
    >
      <Column
        field="name"
        header="Name"
        sortable
      >
        <template #body="{ data: rowData }">
          <span class="font-medium text-kb-fg">{{ rowData.name }}</span>
        </template>
      </Column>
      <Column
        field="member_type"
        header="Type"
      >
        <template #body="{ data: rowData }">
          <SharedMemberTypeBadge
            :member-type="rowData.member_type || 'member'"
            :is-redshirt-lieutenant="rowData.isRedshirtLieutenant === true"
            compact
          />
        </template>
      </Column>
      <Column
        field="status"
        header="Dues"
      >
        <template #body="{ data: rowData }">
          <span
            class="kb-tag"
            :class="duesTagClass(duesStatusFor(rowData).statusType)"
          >{{ duesStatusFor(rowData).tagValue }}</span>
        </template>
      </Column>
      <Column
        field="children"
        header="Kids"
      >
        <template #body="{ data: rowData }">
          <span
            v-if="getChildrenCount(rowData) > 0"
            class="kb-sk-num"
          >{{ getChildrenCount(rowData) }}</span>
          <span
            v-else
            class="text-kb-dim"
          >—</span>
        </template>
      </Column>
      <Column field="emergency_contact">
        <template #header>
          <span v-tooltip.top="'Emergency contact'">ICE</span>
        </template>
        <template #body="{ data: rowData }">
          <button
            v-if="getEmergencyContacts(rowData).length > 0"
            v-tooltip.top="formatEmergencyContactsTooltip(rowData)"
            type="button"
            class="kb-sk-ice"
            aria-label="Call emergency contact"
            @click.stop="callEmergencyContact(rowData)"
          >
            <i class="pi pi-phone" />
            <span
              v-if="getEmergencyContacts(rowData).length > 1"
              class="kb-sk-num"
            >{{ getEmergencyContacts(rowData).length }}</span>
          </button>
          <span
            v-else
            class="text-kb-dim"
          >—</span>
        </template>
      </Column>
      <Column field="checkedIn">
        <template #header>
          <span v-tooltip.top="'Checked in'">In</span>
        </template>
        <template #body="{ data: rowData }">
          <i
            v-if="getMemberCheckinStatus(rowData.id)"
            class="pi pi-check text-kb-accent !text-xs"
            role="img"
            aria-label="Checked in"
          />
          <span
            v-else
            class="text-kb-dim"
          >—</span>
        </template>
      </Column>
      <Column
        v-if="subkreweDuesEnabled"
        field="subkreweDuesStatus"
      >
        <template #header>
          <span v-tooltip.top="`SubKrewe dues (${paradeYear})`">SK dues</span>
        </template>
        <template #body="{ data: rowData }">
          <span
            v-if="!isDuesApplicableForMember(rowData)"
            class="text-kb-dim"
          >—</span>
          <span
            v-else
            class="kb-tag"
            :class="subkreweDuesTag(rowData).tagClass"
          >{{ subkreweDuesTag(rowData).label }}</span>
        </template>
      </Column>
      <Column
        field="actions"
        body-class="whitespace-nowrap"
      >
        <template #body="{ data: rowData }">
          <div class="kb-sk-actions">
            <button
              v-tooltip.top="{ value: 'Email', showDelay: 300 }"
              type="button"
              class="kb-sk-act"
              aria-label="Email"
              @click.stop="emailMember(rowData)"
            >
              <i class="pi pi-envelope" />
            </button>
            <button
              v-if="rowData.phone"
              v-tooltip.top="{ value: 'Call', showDelay: 300 }"
              type="button"
              class="kb-sk-act"
              aria-label="Call"
              @click.stop="callMember(rowData)"
            >
              <i class="pi pi-phone" />
            </button>
            <button
              v-if="canToggleLt(rowData)"
              v-tooltip.top="{ value: rowData.isRedshirtLieutenant ? 'Demote Red Shirt Lt' : 'Promote to Red Shirt Lt.', showDelay: 300 }"
              type="button"
              class="kb-sk-act"
              :aria-label="rowData.isRedshirtLieutenant ? 'Demote Red Shirt Lt' : 'Promote to Red Shirt Lt.'"
              @click.stop="toggleLt(rowData)"
            >
              <i
                v-if="rowData.isRedshirtLieutenant"
                class="pi pi-angle-double-down"
              />
              <i
                v-else
                class="pi pi-angle-double-up"
              />
            </button>
            <button
              v-if="canRemove(rowData)"
              v-tooltip.top="{ value: 'Remove member', showDelay: 300 }"
              type="button"
              class="kb-sk-act kb-sk-act-danger"
              aria-label="Remove member"
              @click.stop="removeMember(rowData)"
            >
              <i class="pi pi-trash" />
            </button>
            <!-- The wrapper carries the tooltip (a disabled button gets no
                 pointer events) and keeps a click here from opening the row -->
            <span
              v-else-if="!props.isRedshirtAdmin && rowData.member_type === 'captain'"
              v-tooltip.top="{ value: `You can't delete a captain before demoting them`, showDelay: 300 }"
              class="inline-flex"
              @click.stop
            >
              <button
                type="button"
                class="kb-sk-act"
                :disabled="true"
                aria-disabled="true"
                aria-label="Remove member"
              >
                <i class="pi pi-trash" />
              </button>
            </span>
          </div>
        </template>
      </Column>
    </DataTable>

    <!-- Dialog for adding member -->
    <AdminMemberSearchDialog
      :visible="showAddMemberDialog"
      :title="props.isRedshirtAdmin ? 'Add Red Shirt to SubKrewe' : 'Add Member to SubKrewe'"
      :search-label="props.isRedshirtAdmin ? 'Search for a red shirt' : 'Search for a member'"
      placeholder="Type name or email"
      :filter-function="filterPotentialMembers"
      @update:visible="showAddMemberDialog = $event"
      @item-selected="selectMemberAndAssign"
    />
  </div>
</template>

<script setup lang="ts">
import type { DataTableRowClickEvent } from 'primevue/datatable'
import { computed, markRaw, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { AdminMemberDrawerContent } from '#components'
import { getMemberDuesStatus } from '~/composables/useShouldPayDues.js'
import type { DuesStatus } from '~/shared/dues/status.js'
import type { Member } from '~/shared/types/member'
import type { SubKrewe } from '~/shared/types/subkrewe'
import type { SubkreweDuesStatus } from '~/shared/types/subkreweDues.js'
import { isSubkreweDuesApplicable, isSubkreweDuesEnabled } from '~/shared/utils/subkreweDues.js'
import { duesTagClass } from '~/utils/duesTag'
import { searchMembers } from '~/utils/memberSearch'

interface Props {
  subkrewe: SubKrewe
  isRedshirtAdmin?: boolean
  isAdmin?: boolean
}

const props = defineProps<Props>()
const emit = defineEmits(['update'])

const subkrewesStore = useSubKrewesStore()
const membersStore = useMembersStore()
const { getBulkCheckinStatus } = useCheckinStatus()
const toast = useToast()
const confirm = useConfirm()
const { setInternalNavigation, openDrawer } = useDrawer()

// State
const membersLoading = ref(false)
const showAddMemberDialog = ref(false)
const checkinStatuses = ref<Record<string, boolean>>({})

// SubKrewe dues (admin read-only view)
const duesStore = useSubkreweDuesStore()
const { paradeYear } = useParadeYear()
const subkreweDuesEnabled = computed(() => isSubkreweDuesEnabled(props.subkrewe, paradeYear.value))

const isDuesApplicableForMember = (member: Member) =>
  isSubkreweDuesApplicable(member, props.subkrewe, paradeYear.value)

// Phosphor tag for the SubKrewe's own dues: paid is OK, anything still owed
// (unpaid or partial) needs attention, waived is neutral
const subkreweDuesTag = (member: Member): { label: string, tagClass: string } => {
  const entry = duesStore.getEntry(props.subkrewe.id, paradeYear.value, member.id)
  const status: SubkreweDuesStatus = entry?.status ?? 'unpaid'
  switch (status) {
    case 'paid': return { label: 'Paid', tagClass: 'kb-tag-ok' }
    case 'partial': return { label: 'Partial', tagClass: 'kb-tag-warn' }
    case 'waived': return { label: 'Waived', tagClass: 'kb-tag-neutral' }
    case 'unpaid':
    default: return { label: 'Unpaid', tagClass: 'kb-tag-warn' }
  }
}

// The SubKrewe's dues entries, live while this list shows them
let stopDuesEntries: (() => void) | null = null
watch(
  [() => props.subkrewe.id, () => subkreweDuesEnabled.value, () => paradeYear.value],
  ([subkreweId, enabled, year]) => {
    stopDuesEntries?.()
    stopDuesEntries = enabled && subkreweId ? duesStore.subscribeEntriesForSubkrewe(subkreweId, year) : null
  },
  { immediate: true },
)
onBeforeUnmount(() => stopDuesEntries?.())

// Members array with reactive updates
const members = computed(() => {
  const list = props.subkrewe.members || []
  return props.isRedshirtAdmin ? list.filter(m => m.member_type === 'redshirt') : list
})

const memberCountLabel = computed(() => {
  const count = members.value.length
  if (props.isRedshirtAdmin) return `${count} ${count === 1 ? 'red shirt' : 'red shirts'}`
  return `${count} ${count === 1 ? 'member' : 'members'}`
})

// Member dues, the same status (and tag) the admin Members table shows
const duesStatusById = computed(() => new Map(members.value.map(member => [member.id, getMemberDuesStatus(member)])))
const duesStatusFor = (member: Member): DuesStatus => duesStatusById.value.get(member.id) ?? getMemberDuesStatus(member)

// Get member check-in status
function getMemberCheckinStatus(memberId: string): boolean {
  return checkinStatuses.value[memberId] || false
}

// Load members data
const loadMembers = async () => {
  membersLoading.value = true
  try {
    // Always load members if not already loaded
    if (members.value.length === 0) {
      await subkrewesStore.loadSubkreweMembers(props.subkrewe)
    }
    // Always load check-in statuses
    await loadCheckinStatuses()
  }
  catch (error) {
    console.error('Error loading members:', error)
  }
  finally {
    membersLoading.value = false
  }
}

// Load check-in statuses
async function loadCheckinStatuses() {
  if (members.value.length === 0) return

  try {
    const statusArray = await getBulkCheckinStatus(members.value)
    const statusMap: Record<string, boolean> = {}
    statusArray.forEach((status) => {
      statusMap[status.member.id] = status.checkinInfo?.checkedIn || false
    })
    checkinStatuses.value = statusMap
  }
  catch (error) {
    console.error('Error loading check-in statuses:', error)
  }
}

// Who is eligible to join, independent of what has been typed. Kept as its
// own computed so eligibility is not re-evaluated on every keystroke.
const eligibleMembers = computed(() =>
  membersStore.membersArray.filter(member =>
    // Not already in this subkrewe
    (!member.subkrewe || member.subkrewe.id !== props.subkrewe.id)
    // Not a captain of another subkrewe
    && member.member_type !== 'captain'
    // If redshirt admin, only show redshirts
    && (!props.isRedshirtAdmin || member.member_type === 'redshirt'),
  ),
)

// Filter potential members
const filterPotentialMembers = (searchTerm: string) => searchMembers(eligibleMembers.value, searchTerm)

// View member details
const viewMemberDetails = (member: Member) => {
  if (!member) return

  setInternalNavigation(true)

  openDrawer({
    title: member.name || 'Member Details',
    data: member,
    component: markRaw(AdminMemberDrawerContent),
  })
}

// Clicking a row (or Enter on a focused row) opens the member
const onRowClick = (event: DataTableRowClickEvent) => {
  viewMemberDetails(event.data as Member)
}

// Remove member
const canRemove = (member: Member) => {
  if (props.isRedshirtAdmin) return member.member_type === 'redshirt'
  return member.member_type !== 'captain'
}

const removeMember = (member: Member) => {
  if (props.isRedshirtAdmin && member.member_type !== 'redshirt') return
  confirm.require({
    message: `Remove ${member.name} from "${props.subkrewe.name}"?`,
    header: 'Remove member',
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: 'Remove',
    rejectLabel: 'Cancel',
    acceptClass: 'p-button-danger',
    accept: async () => {
      try {
        const success = await subkrewesStore.removeMemberFromSubKrewe(
          props.subkrewe,
          member,
        )

        if (success) {
          toast.add({
            severity: 'success',
            summary: 'Member Removed',
            detail: `${member.name} has been removed from "${props.subkrewe.name}"`,
            life: 3000,
          })

          emit('update', { members: props.subkrewe.members })
        }
        else {
          toast.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to remove member',
            life: 3000,
          })
        }
      }
      catch (error) {
        toast.add({
          severity: 'error',
          summary: 'Error',
          detail: `${error}`,
          life: 3000,
        })
      }
    },
  })
}

// Add member
const selectMemberAndAssign = (member: Member) => {
  if (!member) return

  selectPotentialMember(member)
}

const selectPotentialMember = async (member: Member) => {
  try {
    const success = await subkrewesStore.addMemberToSubKrewe(
      props.subkrewe,
      member,
    )

    if (success) {
      toast.add({
        severity: 'success',
        summary: 'Member Added',
        detail: `${member.name} has been added to "${props.subkrewe.name}"`,
        life: 3000,
      })

      showAddMemberDialog.value = false

      // Refresh members list
      await subkrewesStore.loadSubkreweMembers(props.subkrewe)

      emit('update', { members: props.subkrewe.members })
    }
    else {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to add member',
        life: 3000,
      })
    }
  }
  catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: `${error}`,
      life: 3000,
    })
  }
}

// Initialize on mount
onMounted(() => {
  loadMembers()
})

// Watch for member changes and reload check-in statuses
watch(members, () => {
  if (members.value.length > 0) {
    loadCheckinStatuses()
  }
}, { immediate: true })

// Get children count
const getChildrenCount = (member: Member): number => {
  if (!member.children) return 0
  return Object.keys(member.children).length
}

// Redshirt Lieutenant toggle (match captain MembersTable behavior)
const isFreeFloating = computed(() => props.subkrewe?.specialty?.includes('redshirtFreeFloating') === true)
const canToggleLt = (member: Member) => (props.isRedshirtAdmin || props.isAdmin) && isFreeFloating.value && member.member_type === 'redshirt'
const toggleLt = async (member: Member) => {
  const makeLt = !member.isRedshirtLieutenant
  const ok = await membersStore.toggleRedshirtLieutenant(member, makeLt)
  if (ok) {
    toast.add({
      severity: 'success',
      summary: makeLt ? 'Promoted' : 'Demoted',
      detail: `${member.name} ${makeLt ? 'is now' : 'is no longer'} a Red Shirt Lieutenant`,
      life: 2500,
    })
  }
  else {
    toast.add({ severity: 'error', summary: 'Error', detail: 'Could not update lieutenant status', life: 3000 })
  }
}

// Quick actions
const emailMember = (member: Member) => {
  if (!member?.email) return
  window.location.href = `mailto:${member.email}`
}

const callMember = (member: Member) => {
  if (!member?.phone) return
  // Strip spaces and non-dial characters except + and digits
  const normalized = member.phone.replace(/[^+\d]/g, '')
  window.location.href = `tel:${normalized}`
}

// Get emergency contacts array (handles both new and legacy formats)
const getEmergencyContacts = (member: Member) => {
  if (member.emergency_contacts && member.emergency_contacts.length > 0) {
    return member.emergency_contacts
  }
  if (member.emergency_contact?.phone) {
    return [member.emergency_contact]
  }
  return []
}

// Format tooltip for multiple emergency contacts
const formatEmergencyContactsTooltip = (member: Member) => {
  const contacts = getEmergencyContacts(member)
  return contacts
    .map(c => `${c.name || 'Emergency'}${c.relationship ? ` (${c.relationship})` : ''}: ${c.phone}`)
    .join('\n')
}

const callEmergencyContact = (member: Member) => {
  const contacts = getEmergencyContacts(member)
  if (contacts.length === 0) return
  // Call the first contact
  const normalized = contacts[0]?.phone?.replace(/[^+\d]/g, '')
  if (!normalized) return
  window.location.href = `tel:${normalized}`
}
</script>

<style scoped>
.kb-sk-members :deep(.p-datatable-tbody > tr > td) {
  font-size: 0.875rem;
  vertical-align: middle;
}

.kb-sk-num {
  font: 12px var(--kb-font-mono);
  font-variant-numeric: tabular-nums;
  color: var(--kb-fg);
}

/* Emergency contact: a quiet phone that turns accent when you point at it */
.kb-sk-ice {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 4px;
  margin-left: -4px;
  border: 0;
  border-radius: 3px;
  background: none;
  color: var(--kb-muted);
  cursor: pointer;
}

.kb-sk-ice .pi {
  font-size: 12px;
}

.kb-sk-ice .kb-sk-num {
  font-size: 10.5px;
  color: inherit;
}

.kb-sk-ice:hover {
  color: var(--kb-accent);
}

/* Row actions: dim icons that only show on the row you're pointing at or
   have focused; always shown where there's no hover */
.kb-sk-actions {
  display: flex;
  justify-content: flex-end;
  gap: 2px;
  opacity: 0;
}

.kb-sk-members :deep(tr:hover) .kb-sk-actions,
.kb-sk-members :deep(tr:focus-within) .kb-sk-actions {
  opacity: 1;
}

@media (hover: none), (max-width: 639px) {
  .kb-sk-actions {
    opacity: 1;
  }
}

.kb-sk-act {
  display: inline-grid;
  place-items: center;
  width: 24px;
  height: 24px;
  border: 0;
  border-radius: 3px;
  background: none;
  color: var(--kb-dim);
  cursor: pointer;
}

.kb-sk-act .pi,
.kb-sk-act .iconify {
  font-size: 12px;
}

.kb-sk-act:hover {
  color: var(--kb-accent);
}

.kb-sk-act-danger:hover {
  color: var(--kb-alert);
}

.kb-sk-act:disabled {
  opacity: 0.45;
  pointer-events: none;
}

.kb-sk-ice:focus-visible,
.kb-sk-act:focus-visible {
  outline: 2px solid var(--p-primary-color);
  outline-offset: -2px;
}
</style>
